<script setup lang="ts" generic="K extends string">
import UiIcon from './UiIcon.vue'
import type { IconName } from '../icons'

// Short exclusive choice that changes how the same content is shown (Lista / Cards / Quadro).
// Not for navigation between sections: that is UiTabs. Arrow keys move the choice.
defineProps<{ items: ReadonlyArray<{ key: K; label: string; icon?: IconName }>; label: string }>()
const model = defineModel<K>({ required: true })

function onKey(event: KeyboardEvent, items: ReadonlyArray<{ key: K }>) {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
  event.preventDefault()
  const index = items.findIndex((item) => item.key === model.value)
  const next = items[(index + (event.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length]
  if (!next) return
  model.value = next.key
  const group = event.currentTarget as HTMLElement
  void Promise.resolve().then(() => group.querySelector<HTMLElement>('[aria-checked="true"]')?.focus())
}
</script>

<template>
  <div role="radiogroup" :aria-label="label" class="ui-segmented" @keydown="onKey($event, items)">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      role="radio"
      class="ui-segmented__item"
      :class="{ 'ui-segmented__item--active': model === item.key }"
      :aria-checked="model === item.key"
      :tabindex="model === item.key ? 0 : -1"
      @click="model = item.key"
    >
      <UiIcon v-if="item.icon" :name="item.icon" :size="15" />
      {{ item.label }}
    </button>
  </div>
</template>

<style scoped>
.ui-segmented {
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  border: var(--hairline) solid var(--opaque-separator);
  border-radius: var(--radius-control);
  background: var(--surface-card);
}
.ui-segmented__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: calc(var(--control-height) - 6px);
  padding: 0 10px;
  border: 0;
  border-radius: calc(var(--radius-control) - 2px);
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard);
}
.ui-segmented__item :deep(svg) {
  width: 14px;
  height: 14px;
}
.ui-segmented__item:hover {
  color: var(--text-primary);
}
.ui-segmented__item--active,
.ui-segmented__item--active:hover {
  background: var(--accent);
  color: var(--on-accent);
}
</style>
